import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import TemplateManagement from './pages/TemplateManagement';
import UserDashboard from './pages/UserDashboard';
import ProjectDetail from './pages/ProjectDetail';
import PublicUpload from './pages/PublicUpload';
import PublicProgress from './pages/PublicProgress';
import ModuleSelector from './pages/ModuleSelector';
import ImplementationDashboard from './pages/ImplementationDashboard';
import ImplementationProjectDetail from './pages/ImplementationProjectDetail';
import ImplementationPublicUpload from './pages/ImplementationPublicUpload';
import ImplementationTemplateManagement from './pages/ImplementationTemplateManagement';
import ExpenseCategoryAdmin from './pages/ExpenseCategoryAdmin';
import UserNotifications from './pages/UserNotifications';
import TeamManagement from './pages/TeamManagement';
import ApplicationTemplates from './pages/ApplicationTemplates';
import ImplementationTemplates from './pages/ImplementationTemplates';
import MemberStatistics from './pages/MemberStatistics';
import CRMDashboard from './pages/CRMDashboard';
import CRMCustomerDetail from './pages/CRMCustomerDetail';
import CRMAppointments from './pages/CRMAppointments';
import CRMTasks from './pages/CRMTasks';
import NewsletterList from './pages/NewsletterList';
import NewsletterEditor from './pages/NewsletterEditor';
import CRMSettings from './pages/CRMSettings';
import CRMBulkImport from './pages/CRMBulkImport';
import HelpCenter from './pages/HelpCenter';
import NotificationAssistant from './pages/NotificationAssistant';
import Reminders from './pages/Reminders';
import TermsOfService from './components/TermsOfService';
import { Toaster } from './components/ui/sonner';

function App() {
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    const storedUser = localStorage.getItem('espa_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('espa_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('espa_user');
  };

  const handleTermsAccepted = () => {
    // Update user state with terms_accepted = true
    const updatedUser = { ...user, terms_accepted: true };
    setUser(updatedUser);
    localStorage.setItem('espa_user', JSON.stringify(updatedUser));
  };

  // Check if user needs to accept terms (logged in but not accepted)
  const needsTermsAcceptance = user && !user.is_admin && !user.terms_accepted;

  // Show Terms of Service if user hasn't accepted yet
  if (needsTermsAcceptance) {
    return (
      <div className="App">
        <TermsOfService user={user} onAccept={handleTermsAccepted} />
        <Toaster position="top-right" />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Landing Pages */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          
          {/* Module Selector - After Login */}
          <Route 
            path="/select-module" 
            element={
              user && !user.is_admin ? 
                <ModuleSelector user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Team Management Route - Only for Owners */}
          <Route 
            path="/team" 
            element={
              user && !user.is_admin && user.role !== 'member' ? 
                <TeamManagement user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Member Statistics Route */}
          <Route 
            path="/team/statistics/:memberId" 
            element={
              user && !user.is_admin ? 
                <MemberStatistics user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Admin Routes */}
          <Route 
            path="/admin" 
            element={
              user?.is_admin ? 
                <AdminDashboard user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/admin/templates" 
            element={
              user?.is_admin ? 
                <TemplateManagement user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/admin/implementation-templates" 
            element={
              user?.is_admin ? 
                <ImplementationTemplateManagement user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/admin/expense-categories" 
            element={
              user?.is_admin ? 
                <ExpenseCategoryAdmin user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/admin/users/:userId/notifications" 
            element={
              user?.is_admin ? 
                <UserNotifications user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Expense Categories for Owners */}
          <Route 
            path="/expense-categories" 
            element={
              user && !user.is_admin && user.role !== 'member' ? 
                <ExpenseCategoryAdmin user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Application Module Routes */}
          <Route 
            path="/dashboard" 
            element={
              user && !user.is_admin ? 
                <UserDashboard user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/projects/:projectId" 
            element={
              user ? 
                <ProjectDetail user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route path="/upload/:shareId" element={<PublicUpload />} />
          
          {/* Public Progress View Routes */}
          <Route path="/progress/app/:shareId" element={<PublicProgress type="app" />} />
          <Route path="/progress/impl/:shareId" element={<PublicProgress type="impl" />} />
          
          {/* Application Templates Route */}
          <Route 
            path="/application-templates" 
            element={
              user && !user.is_admin ? 
                <ApplicationTemplates user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Implementation Templates Route */}
          <Route 
            path="/implementation-templates" 
            element={
              user && !user.is_admin ? 
                <ImplementationTemplates user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Implementation Module Routes */}
          <Route 
            path="/implementation" 
            element={
              user && !user.is_admin ? 
                <ImplementationDashboard user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/implementation/projects/:projectId" 
            element={
              user ? 
                <ImplementationProjectDetail user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route path="/impl-upload/:shareId" element={<ImplementationPublicUpload />} />
          
          {/* CRM Routes */}
          <Route 
            path="/crm" 
            element={
              user && !user.is_admin ? 
                <CRMDashboard user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/crm/customers/:customerId" 
            element={
              user && !user.is_admin ? 
                <CRMCustomerDetail user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/crm/appointments" 
            element={
              user && !user.is_admin ? 
                <CRMAppointments user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/crm/tasks" 
            element={
              user && !user.is_admin ? 
                <CRMTasks user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/crm/settings" 
            element={
              user && !user.is_admin && user.role !== 'member' ? 
                <CRMSettings user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/crm/bulk-import" 
            element={
              user && !user.is_admin && user.role !== 'member' ? 
                <CRMBulkImport user={user} onLogout={handleLogout} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Help Center Route */}
          <Route 
            path="/help-center" 
            element={
              user && !user.is_admin ? 
                <HelpCenter user={user} /> : 
                <Navigate to="/login" />
            } 
          />
          
          {/* Notification Assistant Route */}
          <Route 
            path="/notification-assistant" 
            element={
              user && !user.is_admin ? 
                <NotificationAssistant user={user} /> : 
                <Navigate to="/login" />
            } 
          />

          {/* Reminders Route */}
          <Route
            path="/reminders"
            element={
              user && !user.is_admin ?
                <Reminders user={user} /> :
                <Navigate to="/login" />
            }
          />
          
          {/* Newsletter Routes */}
          <Route 
            path="/newsletter" 
            element={
              user && !user.is_admin ? 
                <NewsletterList user={user} /> : 
                <Navigate to="/login" />
            } 
          />
          <Route 
            path="/newsletter/:newsletterId" 
            element={
              user && !user.is_admin ? 
                <NewsletterEditor user={user} /> : 
                <Navigate to="/login" />
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;